import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { BatchesSummary } from 'src/app/feature-modules/home/model/BatchesSummary';
import { DevicesSummary } from 'src/app/feature-modules/home/model/DevicesSummary';
import { TemplatesSummary } from 'src/app/feature-modules/home/model/TemplatesSummary';
import { HomeDashboardService } from 'src/app/feature-modules/home/service/home-dashboard.service';
import { DataService } from '../../common-services/data.service';
import { SharedService } from '../../common-services/shared.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { TenantDetails } from 'src/app/core/model';

@Component({
  selector: 'app-metrics',
  templateUrl: './metrics.component.html',
  styleUrls: ['./metrics.component.scss']
})
export class MetricsComponent implements OnInit, OnDestroy {

  @Input() pageType: string;

  devicesSummaryDetails: DevicesSummary;
  templatesSummaryDetails: TemplatesSummary;
  batchesSummaryDetails: BatchesSummary;
  allTenants: boolean = true;
  tenantId: string;

  private _subscriptions: Subscription[] = [];

  constructor(private _homeDashboardService: HomeDashboardService, public jwtHelper: JwtHelperService,
    private _sharedService: SharedService, private _dataService: DataService) { }

  ngOnInit(): void {
    this.tenantCheck();
    if (this.pageType === 'device-listing')
      this._devicesSummary();
    else if (this.pageType === 'template-listing')
      this._templatesSummary();
    else
      this._batchesSummary();

    this._subscriptions.push(this._dataService?.events?.subscribe((res: { args: [], name: string }) => {
      if (res?.name === 'apiRefresh' || res.name === 'tenantChanged' || res.name === 'addDeviceRefresh') {
        this.tenantCheck();
        if (this.pageType === 'device-listing')
          this._devicesSummary();
        else if (this.pageType === 'template-listing')
          this._templatesSummary();
        else
          this._batchesSummary();
      }
    }));
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  /**
   * Tenants check
   */
  tenantCheck() {
    this.tenantId = this._sharedService?.getTenantId();
    this.allTenants = this.tenantId ? false : true;
  }

  /* Get devices summary details */
  private _devicesSummary() {
    this._subscriptions.push(this._homeDashboardService.devicesSummary().subscribe(
      {
        next: (result: DevicesSummary) => {
          this.devicesSummaryDetails = result;
          let disableDevice = this.devicesSummaryDetails?.totalDevices > this._fetchLimitFromConfig('device');
          this._dataService?.broadcast('disableAddDevice', disableDevice);
          if (disableDevice && !this.allTenants) {
            this._sharedService.toastrHandler({ type: 'error', msg: 'Maximum Devices Created' });
          }
        },
        error: (errorResponse: HttpErrorResponse) => {
          this._sharedService.toastrHandler({ type: 'Error', msg: errorResponse?.error?.response?.message });
        }
      }));
  }

  /* Get templates summary details */
  private _templatesSummary() {
    this._subscriptions.push(this._homeDashboardService.templatesSummary().subscribe(
      {
        next: (result: TemplatesSummary) => {
          this.templatesSummaryDetails = result;
        },
        error: (errorResponse: HttpErrorResponse) => {
          this._sharedService.toastrHandler({ type: 'Error', msg: errorResponse?.error?.response?.message });
        }
      }));
  }

  /* Get batches summary details */
  private _batchesSummary() {
    this._subscriptions.push(this._homeDashboardService.batchesSummary().subscribe(
      {
        next: (result: BatchesSummary) => {
          this.batchesSummaryDetails = result;
          let disableBatch = this.batchesSummaryDetails?.totalBatches > this._fetchLimitFromConfig('batch');
          this._dataService?.broadcast('disableAddBatch', disableBatch);
          if (disableBatch && !this.allTenants) {
            this._sharedService.toastrHandler({ type: 'error', msg: 'Maximum Batches Created' });
          }
        },
        error: (errorResponse: HttpErrorResponse) => {
          this._sharedService.toastrHandler({ type: 'Error', msg: errorResponse?.error?.response?.message });
        }
      }));
  }

  /**
   * Fetchs limit from config
   * @param type 
   * @returns  
   */
  private _fetchLimitFromConfig(type: string) {
    const storedTenantList: TenantDetails[] = JSON.parse(localStorage?.getItem('tenantList'));
    const token = (storedTenantList?.find(tenant => tenant?._id === this.tenantId))?.license?.key;
    const decodedToken = this.jwtHelper?.decodeToken(token);
    return type === 'device' ? decodedToken?.noOfDevices : decodedToken?.noOfBatches;
  }
}